import { ArrowLeft, Loader2, Save } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

interface Weight {
  id: string;
  profileId: string;
  metricId: string;
  weight: string;
}

interface Metric {
  id: string;
  name: string;
  categoryId: string;
  sourceType: string;
}

interface Category {
  id: string;
  name: string;
}

interface WeightEditorProps {
  profileId: string;
  onBack: () => void;
}

export function WeightEditor({ profileId, onBack }: WeightEditorProps) {
  const [weights, setWeights] = useState<Weight[]>([]);
  const [metrics, setMetrics] = useState<Metric[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editedWeights, setEditedWeights] = useState<Record<string, string>>({});

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [wRes, mRes, cRes] = await Promise.all([
        fetch(`/api/admin/profiles/${profileId}/weights`),
        fetch("/api/admin/metrics"),
        fetch("/api/admin/categories"),
      ]);
      const [wData, mData, cData] = await Promise.all([wRes.json(), mRes.json(), cRes.json()]);
      setWeights(wData.data ?? []);
      setMetrics(mData.data ?? []);
      setCategories(cData.data ?? []);

      const initial: Record<string, string> = {};
      for (const w of wData.data ?? []) {
        initial[w.metricId] = w.weight;
      }
      setEditedWeights(initial);
    } catch {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  }, [profileId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  async function handleSave(metricId: string) {
    const weight = editedWeights[metricId];
    if (!weight) return;
    setSaving(true);
    try {
      const res = await fetch(`/api/admin/profiles/${profileId}/weights`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ metricId, weight: Number(weight) }),
      });
      if (!res.ok) throw new Error();
      toast.success("Weight updated");
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  }

  if (loading)
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );

  const weightMap = new Map(weights.map((w) => [w.metricId, w.weight]));
  const catMap = new Map(categories.map((c) => [c.id, c.name]));
  const grouped = new Map<string, Metric[]>();
  for (const m of metrics) {
    const existing = grouped.get(m.categoryId) ?? [];
    existing.push(m);
    grouped.set(m.categoryId, existing);
  }

  return (
    <div className="space-y-6">
      <Button variant="ghost" onClick={onBack} className="gap-2">
        <ArrowLeft className="h-4 w-4" /> Back to Profiles
      </Button>

      {Array.from(grouped.entries()).map(([catId, catMetrics]) => (
        <div key={catId}>
          <h3 className="text-lg font-semibold mb-3">{catMap.get(catId) ?? catId}</h3>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[500px]">
              <thead>
                <tr className="border-b text-left text-sm text-muted-foreground">
                  <th className="pb-3 font-medium">Metric</th>
                  <th className="pb-3 font-medium">Source</th>
                  <th className="pb-3 font-medium w-32">Weight</th>
                  <th className="pb-3 font-medium w-20" />
                </tr>
              </thead>
              <tbody>
                {catMetrics.map((m) => (
                  <tr key={m.id} className="border-b">
                    <td className="py-3">{m.name}</td>
                    <td className="py-3">
                      <Badge variant={m.sourceType === "automated" ? "default" : "outline"}>
                        {m.sourceType}
                      </Badge>
                    </td>
                    <td className="py-3">
                      <Input
                        type="number"
                        step="0.001"
                        min="0"
                        max="1"
                        value={editedWeights[m.id] ?? weightMap.get(m.id) ?? "0"}
                        onChange={(e) =>
                          setEditedWeights((prev) => ({
                            ...prev,
                            [m.id]: e.target.value,
                          }))
                        }
                        className="w-24"
                      />
                    </td>
                    <td className="py-3">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleSave(m.id)}
                        disabled={saving}
                      >
                        <Save className="h-4 w-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
}
